import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getMockTestingFlagForAPI } from '../constants';
import { mockAttachmentResponse } from '../utils/mockData';
import { getAttachment } from '../utils/api';

/**
 * Decodes a base64 file content string and returns a blob URL.
 * The caller is responsible for revoking the URL when done.
 *
 * @param {string} fileName - The file name, used to determine MIME type
 * @param {string} fileContent - Base64-encoded file content
 * @returns {string} A blob object URL pointing to the decoded file
 */
export const createBlobUrl = (fileName, fileContent) => {
  const fileExtension = fileName.split('.').pop().toLowerCase();
  const fileType =
    fileExtension === 'pdf' ? 'application/pdf' : `image/${fileExtension}`;

  const decoded = atob(fileContent);
  const base64String =
    decoded.split(',')[1] === undefined ? decoded : decoded.split(',')[1];
  const byteCharacters = atob(base64String);
  const byteArray = Uint8Array.from(byteCharacters, char => char.charCodeAt(0));
  const blob = new Blob([byteArray], { type: fileType });
  return window.URL.createObjectURL(blob);
};

/**
 * Fetches attachment data from the API and returns the file name and content.
 * Uses mock data when getMockTestingFlagForAPI() returns true.
 *
 * @param {string} fileId - The attachment ID to fetch
 * @returns {Promise<{ fileName: string, fileContent: string }>} Resolved with the file name and base64 content
 */
export const fetchAttachmentData = fileId => {
  if (getMockTestingFlagForAPI()) {
    return Promise.resolve({
      fileName: mockAttachmentResponse.data.attributes.fileName,
      fileContent: mockAttachmentResponse.data.attributes.fileContent,
    });
  }

  return getAttachment(fileId).then(res => ({
    fileName: res.data.attributes.fileName,
    fileContent: res.data.attributes.fileContent,
  }));
};

/**
 * Renders an accessible download link for a single attachment.
 *
 * On mount, fetches the file from the API, decodes the base64 content, and
 * generates a blob URL that is set as the `href` on a `VaLink`. The blob URL
 * is revoked on unmount to prevent memory leaks.
 *
 * While loading, a `va-loading-indicator` is shown. On error, an inline
 * message scoped to this attachment is shown (no page-level error).
 *
 * @param {{file: { id: string, name: string }}} props
 * @returns {React.ReactElement}
 */
export function DownloadAttachment({ file }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadingFile, setLoadingFile] = useState(true);
  const [fileError, setFileError] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;

    fetchAttachmentData(file.id)
      .then(({ fileName, fileContent }) => {
        if (cancelled) return;
        objectUrl = createBlobUrl(fileName, fileContent);
        setBlobUrl(objectUrl);
        setLoadingFile(false);
      })
      .catch(() => {
        if (cancelled) return;
        setFileError(true);
        setLoadingFile(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file.id]);

  if (loadingFile) {
    return (
      <va-loading-indicator
        message={`Loading ${file.name}...`}
        set-focus={false}
      />
    );
  }

  if (fileError) {
    return (
      <span className="vads-u-color--secondary-dark">
        We couldn‘t load {file.name}. Try refreshing the page.
      </span>
    );
  }

  return (
    <va-link download href={blobUrl} text={file.name} filename={file.name} />
  );
}

DownloadAttachment.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};
